/**
 * WelcomeBanner
 *
 * Lightweight one-time banner that introduces the four most important
 * sidebar areas to a freshly-onboarded user. Renders on every protected
 * page (mounted inside OnboardingGuard) until the user clicks "Got it" /
 * "Verstanden" — dismissal is keyed by `session.user.id`, so different
 * users on the same browser see it independently.
 *
 * Storage is plain `localStorage` (per-device, not synced) — keeps the
 * change inside this app, no plugin/core-frontend touch needed. If we
 * later want cross-device sync, swap the read/write for a typed
 * `setPreference("welcomeBannerDismissed", true)` once the kernel's
 * UserPreferences schema includes the field.
 */

import { useAuth } from '@xhubio-saas/core-frontend/kernel'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@xhubio-saas/core-frontend/ui-components'
import { BarChart3, FileText, Key, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { QuickLinkGrid, type QuickLinkItem } from './QuickLinkGrid'

const STORAGE_KEY_PREFIX = 'welcome-banner-dismissed:'

function dismissKey(userId: string | undefined): string {
  return `${STORAGE_KEY_PREFIX}${userId ?? 'anonymous'}`
}

export function WelcomeBanner() {
  const { t } = useTranslation()
  const { session } = useAuth()
  const userId = session?.user?.id

  // Read initial dismissed-state from localStorage. useState initializer so we
  // only touch storage once per mount.
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true
    return localStorage.getItem(dismissKey(userId)) === 'true'
  })

  // If session.user.id resolves AFTER mount, re-check the storage key —
  // we don't want to show the banner to a user who already dismissed it.
  useEffect(() => {
    if (typeof window === 'undefined') return
    setDismissed(localStorage.getItem(dismissKey(userId)) === 'true')
  }, [userId])

  const handleDismiss = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(dismissKey(userId), 'true')
    }
    setDismissed(true)
  }, [userId])

  if (dismissed) return null

  const links: QuickLinkItem[] = [
    {
      href: '/api-keys',
      icon: Key,
      title: t('onboarding.welcome.links.apiKeys.title'),
      description: t('onboarding.welcome.links.apiKeys.description'),
    },
    {
      href: '/pdf/v2/templates',
      icon: FileText,
      title: t('onboarding.welcome.links.templates.title'),
      description: t('onboarding.welcome.links.templates.description'),
    },
    {
      href: '/usage',
      icon: BarChart3,
      title: t('onboarding.welcome.links.usage.title'),
      description: t('onboarding.welcome.links.usage.description'),
    },
  ]

  const dismissText = t('onboarding.dismiss')

  return (
    <Card className="mb-6 border-primary/20 bg-primary/5">
      <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
        <div>
          <CardTitle className="text-lg">{t('onboarding.welcome.title')}</CardTitle>
          <CardDescription className="mt-1 max-w-2xl">
            {t('onboarding.welcome.description')}
          </CardDescription>
        </div>
        <Button
          variant="ghost"
          size="sm"
          aria-label={dismissText}
          onClick={handleDismiss}
          className="-mr-2 -mt-2"
        >
          <X className="h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent>
        <QuickLinkGrid links={links} layout="even" />
        <div className="mt-4 flex justify-end">
          <Button variant="outline" size="sm" onClick={handleDismiss}>
            {dismissText}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
